// frontend/src/components/team/TeamMemberEditor.js
import React, { useState, useEffect } from 'react';
import { FaSave, FaTimes, FaPlus, FaTrash } from 'react-icons/fa';
import Loader from '../ui/Loader';
import api from '../../utils/api';

const TeamMemberEditor = ({ member, onSave, onCancel, apiAvailable = true }) => {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    bio: '',
    education: '',
    socialMedia: { linkedin: '' },
    achievements: []
  });
  
  const [newAchievement, setNewAchievement] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Load member data when component mounts
  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name || '',
        title: member.title || '',
        bio: member.bio || '',
        education: member.education || '',
        socialMedia: member.socialMedia || { linkedin: '' },
        achievements: member.achievements || []
      });
    }
  }, [member]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSocialMediaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [name]: value
      }
    }));
  };
  
  const addAchievement = () => {
    if (!newAchievement.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      achievements: [...prev.achievements, newAchievement.trim()]
    }));
    
    setNewAchievement('');
  };
  
  const removeAchievement = (index) => {
    setFormData(prev => ({
      ...prev,
      achievements: prev.achievements.filter((_, i) => i !== index)
    }));
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addAchievement();
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.title) {
      setError('姓名和职位是必填项');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      if (!apiAvailable) {
        // If API is not available, just update the local state
        onSave(member.id, formData);
        return;
      }
      
      const updateData = {
        name: formData.name,
        title: formData.title,
        bio: formData.bio,
        education: formData.education || null,
        socialMedia: formData.socialMedia,
        achievements: formData.achievements
      };
      
      try {
        // Make API call to update team member
        const response = await api.put(`/team/${member.id}`, updateData);
        
        if (response.data && response.data.success) {
          onSave(member.id, response.data.member);
        } else {
          throw new Error(response.data?.message || '更新失败');
        }
      } catch (apiError) {
        console.error('API update error:', apiError);
        
        // If API call fails, fall back to local update
        onSave(member.id, formData);
        
        // Show warning that this is a temporary solution
        setError('服务器更新失败，数据已暂时更新但不会永久保存。');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || '表单提交失败');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="team-member-editor bg-white p-4 rounded-xl shadow-lg max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-bold">编辑团队成员信息</h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700"
        >
          <FaTimes size={20} />
        </button>
      </div>
      
      {error && (
        <div className="bg-red-100 text-red-800 p-2 rounded mb-3 text-sm">
          {error}
        </div>
      )}
      
      {!apiAvailable && (
        <div className="bg-yellow-100 text-yellow-800 p-2 rounded mb-3 text-sm">
          注意：API 功能暂时不可用。您的更改将仅在此会话中可见，不会保存到服务器。
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-medium mb-1">
            姓名 <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            required
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-medium mb-1">
            职位 <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            required
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-medium mb-1">
            个人简介
          </label>
          <textarea
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            rows={5}
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-medium mb-1">
            教育背景
          </label>
          <input
            type="text"
            name="education"
            value={formData.education}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            placeholder="例如: 北京大学工商管理学士"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-medium mb-1">
            LinkedIn 主页
          </label>
          <input
            type="url"
            name="linkedin"
            value={formData.socialMedia.linkedin}
            onChange={handleSocialMediaChange}
            className="w-full p-2 border rounded"
            placeholder="https://www.linkedin.com/in/username"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-medium mb-1 mb-2">
            主要成就
          </label>
          
          <div className="space-y-2 mb-2">
            {formData.achievements.map((achievement, index) => (
              <div key={index} className="flex items-center">
                <div className="flex-grow bg-gray-50 p-2 border rounded">
                  {achievement}
                </div>
                <button
                  type="button"
                  onClick={() => removeAchievement(index)}
                  className="ml-2 text-red-600 hover:text-red-800"
                >
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>
          
          <div className="flex items-center">
            <input
              type="text"
              value={newAchievement}
              onChange={(e) => setNewAchievement(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-grow p-2 border rounded"
              placeholder="输入成就后按回车或点击添加"
            />
            <button
              type="button"
              onClick={addAchievement}
              className="ml-2 bg-gray-100 p-2 rounded hover:bg-gray-200"
            >
              <FaPlus />
            </button>
          </div>
        </div>
        
        <div className="flex justify-between sticky bottom-0 pt-2 bg-white">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1 border rounded text-sm text-gray-600 hover:bg-gray-100"
            disabled={saving}
          >
            取消
          </button>
          
          <button
            type="submit"
            className="bg-green-600 text-white px-4 py-1 rounded text-sm hover:bg-green-700 flex items-center justify-center min-w-[80px]"
            disabled={saving}
          >
            {saving ? (
              <Loader size="sm" />
            ) : (
              <>
                <FaSave className="mr-1" />
                保存
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamMemberEditor;